import * as React from "react";
import { Alert, AlertDescription, AlertTitle, Label, RmBodyZone, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, Switch } from "randmarcomps";
import { AlertCircle, Info } from "lucide-react";
import { useGetV4ManufacturerByRouteManufacturerIdAccountQuery, useGetV4ManufacturerByRouteManufacturerIdReturnsQuery } from "randmar-api-client";
import ReturnGrid from "../components/grids/ReturnGrid";
import DataRefreshAssistantCluster from "../components/layout/DataRefreshAssistantCluster";
import RmPageHeader from "../components/layout/RmPageHeader";
import { useManufacturer } from "../contexts/WithManufacturer";
import withRoot from "../withRoot";

function buildReturnsAssistantPrompt(options: {
  manufacturerName: string;
  applicationId: string;
  statusFilterLabel: string;
  total: number;
  topStatuses: Array<{ status: string; count: number }>;
}) {
  const statusBreakdown =
    options.topStatuses.length > 0
      ? `Top statuses: ${options.topStatuses.map((entry) => `${entry.status} (${entry.count})`).join(", ")}.`
      : "No returns are currently listed.";

  return [
    `You are helping with the Manufacturer portal Returns page.`,
    `Manufacturer: ${options.manufacturerName} (ID: ${options.applicationId}).`,
    `Filter: ${options.statusFilterLabel}.`,
    `Total returns in view: ${options.total}.`,
    statusBreakdown,
    `Give the user a quick summary and the next best actions (if any) based on the current list.`,
  ].join("\n");
}

function Returns() {
  const { applicationId } = useManufacturer();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string>("ALL");
  const baseTitleRef = React.useRef<string | null>(null);

  const accountQuery = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const returnsQuery = useGetV4ManufacturerByRouteManufacturerIdReturnsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const manufacturerName = accountQuery.data?.Name?.trim() || "";
  const returns = returnsQuery.data ?? [];
  const isRefreshing = returnsQuery.isFetching;

  React.useEffect(() => {
    if (!applicationId) {
      setLastRefreshedAt(null);
      return;
    }

    if (!isRefreshing && (returnsQuery.isSuccess || returnsQuery.isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [applicationId, isRefreshing, returnsQuery.isError, returnsQuery.isSuccess]);

  const statusOptions = React.useMemo(() => {
    const unique = new Set<string>();
    for (const ret of returns) {
      const normalized = ret.Status?.trim();
      if (normalized) unique.add(normalized);
    }
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [returns]);

  const filteredReturns = React.useMemo(() => {
    if (statusFilter === "ALL") return returns;
    return returns.filter((ret) => (ret.Status || "").trim() === statusFilter);
  }, [returns, statusFilter]);

  const topStatuses = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const ret of returns) {
      const key = ret.Status?.trim() || "Unknown";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [returns]);

  const statusFilterLabel = statusFilter === "ALL" ? "All statuses" : statusFilter;

  const assistantPrompt = React.useMemo(() => {
    return buildReturnsAssistantPrompt({
      manufacturerName: manufacturerName || "Unknown manufacturer",
      applicationId,
      statusFilterLabel,
      total: filteredReturns.length,
      topStatuses,
    });
  }, [applicationId, filteredReturns.length, manufacturerName, statusFilterLabel, topStatuses]);

  React.useEffect(() => {
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title;
    }
    const baseTitle = baseTitleRef.current || document.title;
    const suffix = statusFilter !== "ALL" ? ` — ${statusFilter}` : "";
    document.title = manufacturerName ? `Returns${suffix} — ${manufacturerName} | ${baseTitle}` : `Returns${suffix} | ${baseTitle}`;
    return () => {
      document.title = baseTitle;
    };
  }, [manufacturerName, statusFilter]);

  const isInitialLoad = !returnsQuery.data && (returnsQuery.isLoading || isRefreshing) && !returnsQuery.isError;

  if (isInitialLoad) {
    return (
      <>
        <RmPageHeader
          title="Returns inbox"
          subtitle="Review defective returns received by Randmar warehouses and collaborate with resellers using inline comments."
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={async () => {
                await returnsQuery.refetch();
              }}
              disabled
            />
          )}
        />
        <RmBodyZone className="space-y-8 pb-10" role="status" aria-live="polite">
          <Skeleton className="h-[520px] w-full rounded-2xl" />
        </RmBodyZone>
      </>
    );
  }

  return (
    <>
      <RmPageHeader
        title="Returns inbox"
        subtitle="Review defective returns received by Randmar warehouses and collaborate with resellers using inline comments."
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isRefreshing}
            onRefresh={async () => {
              await returnsQuery.refetch();
            }}
            assistantPromptOptions={assistantPrompt ? { prompt: assistantPrompt } : undefined}
            disabled={!assistantPrompt}
          />
        )}
      />
      <RmBodyZone className="space-y-8 pb-10" aria-busy={isRefreshing}>
      {(returnsQuery.isError || accountQuery.isError) && (
        <Alert className="rounded-2xl border border-rose-100 bg-rose-50 shadow-sm">
          <AlertCircle className="h-4 w-4 text-rose-600" aria-hidden="true" />
          <AlertTitle className="text-slate-900">Couldn’t refresh Returns</AlertTitle>
          <AlertDescription className="text-slate-700">Some data may be out of date. Try refreshing again.</AlertDescription>
        </Alert>
      )}

      <Alert className="rounded-2xl border border-sky-100 bg-sky-50 shadow-sm">
        <Info className="h-4 w-4 text-sky-700" aria-hidden="true" />
        <AlertTitle className="text-slate-900">What counts as a return?</AlertTitle>
        <AlertDescription className="text-slate-700">
          This page shows defective items that resellers have returned to Randmar warehouses. Use the status filter and comments to coordinate next steps.
        </AlertDescription>
      </Alert>

      <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-2">
            <h2 className="text-[20px] font-medium text-slate-900">Filters</h2>
            <p className="text-[13px] text-slate-500">
              Showing <span className="font-semibold text-slate-900">{filteredReturns.length}</span> of{" "}
              <span className="font-semibold text-slate-900">{returns.length}</span> returns.
            </p>
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="min-w-[220px] space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <Switch id="returns-accept-toggle" checked={false} disabled />
              <div className="space-y-0.5">
                <Label htmlFor="returns-accept-toggle" className="text-[13px] font-medium text-slate-900">
                  Accept returns
                </Label>
                <p className="text-[13px] text-slate-500">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ReturnGrid returns={filteredReturns} defaultRowsPerPage={25} />
      </RmBodyZone>
    </>
  );
}

export default withRoot(Returns);
